import type {
  User,
  NavNode,
  ContentData,
  UserRecord,
  UserRole,
  PublishResult,
} from './types';

async function apiFetch<T>(
  path: string,
  options?: RequestInit
): Promise<T> {
  const response = await fetch(path, {
    credentials: 'include',
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(options?.headers || {}),
    },
  });

  if (!response.ok) {
    let message = `API error ${response.status}`;
    try {
      const data = await response.json() as { error?: string };
      if (data.error) message = data.error;
    } catch {
      // ignore parse error
    }
    throw new Error(message);
  }

  return response.json() as Promise<T>;
}

export async function fetchMe(): Promise<{ user: User | null }> {
  return apiFetch<{ user: User | null }>('/api/me');
}

export async function fetchNav(): Promise<{ nav: NavNode[] }> {
  return apiFetch<{ nav: NavNode[] }>('/api/nav');
}

export async function fetchContent(path: string): Promise<ContentData> {
  return apiFetch<ContentData>(`/api/content/${path}`);
}

export async function saveDraft(path: string, content: string): Promise<{ ok: boolean; savedAt: string }> {
  return apiFetch<{ ok: boolean; savedAt: string }>(`/api/content/${path}`, {
    method: 'PUT',
    body: JSON.stringify({ content }),
  });
}

export async function publishContent(
  path: string,
  content: string,
  message?: string
): Promise<PublishResult> {
  return apiFetch<PublishResult>(`/api/publish/${path}`, {
    method: 'POST',
    body: JSON.stringify({ content, message }),
  });
}

export async function uploadImage(
  file: File
): Promise<{ url: string }> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('filename', file.name);

  const response = await fetch('/api/upload', {
    method: 'POST',
    credentials: 'include',
    body: formData,
  });

  if (!response.ok) {
    let message = `Upload error ${response.status}`;
    try {
      const data = await response.json() as { error?: string };
      if (data.error) message = data.error;
    } catch {
      // ignore
    }
    throw new Error(message);
  }

  return response.json() as Promise<{ url: string }>;
}

export async function logout(): Promise<void> {
  await fetch('/api/auth/logout', {
    method: 'POST',
    credentials: 'include',
  });
}

// Admin APIs
export async function fetchUsers(): Promise<{ users: UserRecord[] }> {
  return apiFetch<{ users: UserRecord[] }>('/api/admin/users');
}

export async function grantAccess(
  email: string,
  role: UserRole
): Promise<{ ok: boolean }> {
  return apiFetch<{ ok: boolean }>('/api/admin/users', {
    method: 'POST',
    body: JSON.stringify({ email, role }),
  });
}

export async function updateUserRole(
  email: string,
  role: UserRole
): Promise<{ ok: boolean }> {
  return apiFetch<{ ok: boolean }>(
    `/api/admin/users/${encodeURIComponent(email)}`,
    {
      method: 'PUT',
      body: JSON.stringify({ role }),
    }
  );
}

export async function revokeUser(email: string): Promise<{ ok: boolean }> {
  return apiFetch<{ ok: boolean }>(
    `/api/admin/users/${encodeURIComponent(email)}`,
    { method: 'DELETE' }
  );
}

export async function initDb(): Promise<{ ok: boolean; message: string }> {
  return apiFetch<{ ok: boolean; message: string }>('/api/admin/init', {
    method: 'POST',
  });
}
